import { Mail, Phone, Sparkles, ClipboardList, LogOut } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { logOut } from '../firebase/config';

const REGISTER_FORM_URL = 'https://forms.gle/7UGMMTB3ApLTiKfB8';
const CONTACT_EMAIL = 'loanvcs@aaas.chat';
const CONTACT_PHONE = '0905041891';

export const BetaAccessDeniedPage = () => {
  const { betaBlockInfo, userProfile } = useAuth();
  const { t } = useTranslation(['shared', 'common']);
  const email = betaBlockInfo?.email || userProfile?.email || '';

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      window.location.href = '/login';
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-white p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border bg-background shadow-xl">
        <div className="px-8 pb-8 pt-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 ring-8 ring-amber-50">
            <Sparkles className="h-8 w-8 text-amber-600" />
          </div>

          <h1 className="mt-6 text-2xl font-semibold tracking-tight text-foreground">
            {t('beta.title')}
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            <Trans
              i18nKey="beta.description"
              ns="shared"
              components={{ strong: <strong className="font-semibold text-foreground" /> }}
            />
          </p>

          {email && (
            <div className="mt-5 flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
                <span className="opacity-60">{t('beta.signedInAs')}</span>
                <span className="max-w-[240px] truncate">{email}</span>
              </span>
            </div>
          )}

          <Button asChild size="lg" className="mt-8 w-full sm:w-auto">
            <a href={REGISTER_FORM_URL} target="_blank" rel="noopener noreferrer">
              <ClipboardList className="mr-2 h-4 w-4" />
              {t('beta.registerCta')}
            </a>
          </Button>
        </div>

        <div className="border-t bg-muted/30 px-8 py-5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('beta.contactHeading')}
          </p>
          <div className="mt-2 space-y-1">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="group flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-background"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border group-hover:ring-amber-300">
                <Mail className="h-4 w-4 text-amber-600" />
              </span>
              <span className="text-sm font-medium text-foreground">{CONTACT_EMAIL}</span>
            </a>
            <a
              href={`tel:${CONTACT_PHONE.replace(/\s+/g, '')}`}
              className="group flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-background"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border group-hover:ring-amber-300">
                <Phone className="h-4 w-4 text-amber-600" />
              </span>
              <span className="text-sm font-medium text-foreground">{CONTACT_PHONE}</span>
            </a>
          </div>
          <div className="mt-4 flex justify-center">
            <Button variant="ghost" size="sm" onClick={handleLogout} className="hover:bg-red-500 hover:text-white">
              <LogOut className="mr-2 h-4 w-4" />
              {t('header.signOut')}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};
